/**
 * FERP Portal - Queue List Page (DRR-074: Tabulator DRY Template)
 *
 * EXECUTE Phase: Task routing and work queues.
 * Delegates to TabulatorPrimitiveManager for consistent CRUD across all 25 primitives.
 *
 * Sprint 3a (DRR-125): BarChart of currentSize by queueType from real backend data.
 * No hardcoded data — empty state shown when backend returns no records.
 */

import { useMemo } from 'react';
import { Paper, Stack, Title, Text, Center } from '@mantine/core';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import { usePrimitiveList } from '@/hooks';
import { GqlQueueType } from '@/generated/graphql';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const QUEUE_TYPE_LABELS: Record<string, string> = {
  [GqlQueueType.FAIR]: 'Fair',
  [GqlQueueType.FIFO]: 'FIFO',
  [GqlQueueType.LIFO]: 'LIFO',
  [GqlQueueType.PRIORITY]: 'Priority',
};

function QueueSizeChart() {
  const { items, loading } = usePrimitiveList('queue');

  const chartData = useMemo(() => {
    if (!items.length) return [];
    const totals: Record<string, number> = {};
    for (const item of items) {
      const qType: string = (item.queueType as string) ?? 'UNKNOWN';
      totals[qType] = (totals[qType] ?? 0) + ((item.currentSize as number) ?? 0);
    }
    return Object.entries(totals).map(([queueType, total]) => ({
      queueType: QUEUE_TYPE_LABELS[queueType] ?? queueType,
      currentSize: total,
    }));
  }, [items]);

  return (
    <Paper p="md" withBorder radius="md">
      <Title order={5} mb="xs">
        Current Queue Size by Type
      </Title>
      {!loading && chartData.length === 0 ? (
        <Center h={160}>
          <Text c="dimmed" size="sm">
            No queue data available
          </Text>
        </Center>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <XAxis dataKey="queueType" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={40} allowDecimals={false} />
            <Tooltip formatter={(value: number) => [value.toLocaleString(), 'Items']} />
            <Bar dataKey="currentSize" fill="#69db7c" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}

export function QueueListPage() {
  return (
    <Stack gap="md">
      <QueueSizeChart />
      <TabulatorPrimitiveManager primitiveType="queue" />
    </Stack>
  );
}

export default QueueListPage;
